"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback, useMemo } from "react";
import useCapacitor from "@/hooks/useCapacitor";
import { useAndroidKeyboard } from "@/hooks/useAndroidKeyboard";

// Define the different spaces that need to be measured
interface LayoutSpaces {
  // Space used by persistent UI elements (header, breadcrumb, etc)
  headerSpace: number;

  // Space used by dynamic elements (pinned waves, etc)
  pinnedSpace: number;

  // Space used by tab elements when present
  tabsSpace: number;

  // Space used by spacer elements
  spacerSpace: number;

  // Space used by mobile tabs
  mobileTabsSpace: number;

  // Space used by mobile nav
  mobileNavSpace: number;
  // Available space for content
  contentSpace: number;

  // Whether measurements have completed
  measurementsComplete: boolean;
}
const spacesAreEqual = (a: LayoutSpaces, b: LayoutSpaces) => a.headerSpace === b.headerSpace && a.pinnedSpace === b.pinnedSpace && a.tabsSpace === b.tabsSpace && a.spacerSpace === b.spacerSpace && a.mobileTabsSpace === b.mobileTabsSpace && a.mobileNavSpace === b.mobileNavSpace && a.contentSpace === b.contentSpace && a.measurementsComplete === b.measurementsComplete;

// Helper function to calculate height style
const calculateHeightStyle = (spaces: LayoutSpaces, capacitorSpace: number): React.CSSProperties => {
  // Use dynamic viewport height to avoid extra space on mobile browsers
  const heightCalc = `calc(100dvh - ${spaces.headerSpace}px - ${spaces.pinnedSpace}px - ${spaces.tabsSpace}px - ${spaces.spacerSpace}px - ${spaces.mobileTabsSpace}px - ${spaces.mobileNavSpace}px - ${capacitorSpace}px)`;
  return {
    height: heightCalc,
    maxHeight: heightCalc
  };
};

// Context type definition
// Define valid ref types for type safety
type LayoutRefType = "header" | "pinned" | "tabs" | "spacer" | "mobileTabs" | "mobileNav";
interface LayoutContextType {
  // Calculated spaces
  spaces: LayoutSpaces;

  // Registration system
  registerRef: (refType: LayoutRefType, element: HTMLDivElement | null) => void;

  // Pre-calculated styles for main containers
  contentContainerStyle: React.CSSProperties;

  // Style for chat view
  waveViewStyle: React.CSSProperties;

  // Style for leaderboard view
  leaderboardViewStyle: React.CSSProperties;

  // Style for winners view
  winnersViewStyle: React.CSSProperties;

  // Style for outcome view
  outcomeViewStyle: React.CSSProperties;

  // Style for my votes view
  myVotesViewStyle: React.CSSProperties;

  // Style for FAQ view
  faqViewStyle: React.CSSProperties;

  // Style for notifications view
  notificationsViewStyle: React.CSSProperties;

  // Style for feed view
  myStreamFeedStyle: React.CSSProperties;

  // Style for homepage feed view (excludes header/breadcrumbs, includes tabs)
  homepageFeedStyle: React.CSSProperties;

  // Style for small screen layout feed (includes header and tabs)
  smallScreenFeedStyle: React.CSSProperties;

  // Style for mobile waves view
  mobileWavesViewStyle: React.CSSProperties;

  // Style for mobile about view
  mobileAboutViewStyle: React.CSSProperties;
}

// Default context values
const defaultSpaces: LayoutSpaces = {
  headerSpace: 0,
  pinnedSpace: 0,
  tabsSpace: 0,
  spacerSpace: 0,
  mobileTabsSpace: 0,
  mobileNavSpace: 0,
  contentSpace: 0,
  measurementsComplete: false
};

// Create context
const LayoutContext = createContext<LayoutContextType>({
  spaces: defaultSpaces,
  registerRef: () => {},
  // No-op for default value
  contentContainerStyle: {},
  // Empty style object as default
  waveViewStyle: {},
  // Empty style object as default
  leaderboardViewStyle: {},
  // Empty style object as default
  winnersViewStyle: {},
  // Empty style object as default
  myVotesViewStyle: {},
  // Empty style object as default
  outcomeViewStyle: {},
  // Empty style object as default
  faqViewStyle: {},
  // Empty style object as default for FAQ
  notificationsViewStyle: {},
  // Empty style object as default
  myStreamFeedStyle: {},
  // Empty style object as default
  homepageFeedStyle: {},
  // Empty style object as default
  smallScreenFeedStyle: {},
  // Empty style object as default
  mobileWavesViewStyle: {},
  // Empty style object as default
  mobileAboutViewStyle: {} // Empty style object as default
});

// Provider component
export const LayoutProvider: React.FC<{
  children: ReactNode;
}> = t0 => {
  const $ = _c(67);
  const {
    children
  } = t0;
  const {
    isCapacitor,
    isAndroid,
    isIos
  } = useCapacitor();
  const {
    isVisible: isKeyboardVisible
  } = useAndroidKeyboard();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      header: null,
      pinned: null,
      tabs: null,
      spacer: null,
      mobileTabs: null,
      mobileNav: null
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const refMap = useRef(t1);
  const resizeObserverRef = useRef(null);
  const [spaces, setSpaces] = useState(defaultSpaces);
  const calculateSpacesRef = useRef(_temp);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (refType, element) => {
      if (refMap.current[refType] === element) {
        return;
      }
      const previousElement = refMap.current[refType];
      refMap.current[refType] = element;
      if (resizeObserverRef.current) {
        if (previousElement) {
          resizeObserverRef.current.unobserve(previousElement);
        }
        if (element) {
          resizeObserverRef.current.observe(element);
        }
      }
      requestAnimationFrame(() => calculateSpacesRef.current());
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const registerRef = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      let headerHeight = 0;
      let pinnedHeight = 0;
      let tabsHeight = 0;
      let spacerHeight = 0;
      let mobileTabsHeight = 0;
      let mobileNavHeight = 0;
      const headerElement = refMap.current.header;
      const pinnedElement = refMap.current.pinned;
      const tabsElement = refMap.current.tabs;
      const spacerElement = refMap.current.spacer;
      const mobileTabsElement = refMap.current.mobileTabs;
      const mobileNavElement = refMap.current.mobileNav;
      if (headerElement) {
        ;
        try {
          const rect = headerElement.getBoundingClientRect();
          headerHeight = rect.height;
        } catch (t4) {
          const e = t4;
          console.error("Error measuring header element:", e);
        }
      }
      if (pinnedElement) {
        ;
        try {
          const rect_0 = pinnedElement.getBoundingClientRect();
          pinnedHeight = rect_0.height;
        } catch (t5) {
          const e_0 = t5;
          console.error("Error measuring pinned element:", e_0);
        }
      }
      if (tabsElement) {
        ;
        try {
          const rect_1 = tabsElement.getBoundingClientRect();
          tabsHeight = rect_1.height;
        } catch (t6) {
          const e_1 = t6;
          console.error("Error measuring tabs element:", e_1);
        }
      }
      if (spacerElement) {
        ;
        try {
          const rect_2 = spacerElement.getBoundingClientRect();
          spacerHeight = rect_2.height;
        } catch (t7) {
          const e_2 = t7;
          console.error("Error measuring spacer element:", e_2);
        }
      }
      if (mobileTabsElement) {
        ;
        try {
          const rect_3 = mobileTabsElement.getBoundingClientRect();
          mobileTabsHeight = rect_3.height;
        } catch (t8) {
          const e_3 = t8;
          console.error("Error measuring mobile tabs element:", e_3);
        }
      }
      if (mobileNavElement) {
        ;
        try {
          const rect_4 = mobileNavElement.getBoundingClientRect();
          mobileNavHeight = rect_4.height;
        } catch (t9) {
          const e_4 = t9;
          console.error("Error measuring mobile nav element:", e_4);
        }
      }
      const totalOccupiedSpace = headerHeight + pinnedHeight + tabsHeight + spacerHeight + mobileTabsHeight + mobileNavHeight;
      const calculatedContentSpace = Math.max(0, window.innerHeight - totalOccupiedSpace);
      const nextSpaces = {
        headerSpace: headerHeight,
        pinnedSpace: pinnedHeight,
        tabsSpace: tabsHeight,
        spacerSpace: spacerHeight,
        mobileTabsSpace: mobileTabsHeight,
        mobileNavSpace: mobileNavHeight,
        contentSpace: calculatedContentSpace,
        measurementsComplete: true
      };
      setSpaces(prevSpaces => spacesAreEqual(prevSpaces, nextSpaces) ? prevSpaces : nextSpaces);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const calculateSpaces = t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(calculateSpaces);
      });
      Object.entries(refMap.current).forEach(t6 => {
        const [, element_0] = t6;
        if (element_0) {
          resizeObserverRef.current?.observe(element_0);
        }
      });
      window.addEventListener("resize", calculateSpaces);
      calculateSpaces();
      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        window.removeEventListener("resize", calculateSpaces);
      };
    };
    t5 = [calculateSpaces];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      calculateSpacesRef.current = calculateSpaces;
    };
    t7 = [calculateSpaces];
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  useEffect(t6, t7);
  let t8;
  bb0: {
    if (!spaces.measurementsComplete) {
      let t9;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = {};
        $[7] = t9;
      } else {
        t9 = $[7];
      }
      t8 = t9;
      break bb0;
    }
    const t9 = `calc(100dvh - ${spaces.headerSpace}px - ${spaces.spacerSpace}px)`;
    let t10;
    if ($[8] !== t9) {
      t10 = {
        height: t9,
        display: "flex"
      };
      $[8] = t9;
      $[9] = t10;
    } else {
      t10 = $[9];
    }
    t8 = t10;
  }
  const contentContainerStyle = t8;
  let t9;
  bb1: {
    if (!spaces.measurementsComplete) {
      let t10;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = {};
        $[10] = t10;
      } else {
        t10 = $[10];
      }
      t9 = t10;
      break bb1;
    }
    let capSpace = 0;
    if (isAndroid) {
      capSpace = isKeyboardVisible ? 0 : 128;
    } else {
      if (isIos || isCapacitor) {
        capSpace = 20;
      }
    }
    let t10;
    if ($[11] !== capSpace || $[12] !== spaces) {
      const adjustedSpaces = {
        ...spaces,
        mobileNavSpace: 0
      };
      t10 = calculateHeightStyle(adjustedSpaces, capSpace);
      $[11] = capSpace;
      $[12] = spaces;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    const style = t10;
    if (isAndroid) {
      let t11;
      if ($[14] !== style) {
        t11 = {
          ...style,
          transition: "height 75ms ease-out, max-height 75ms ease-out"
        };
        $[14] = style;
        $[15] = t11;
      } else {
        t11 = $[15];
      }
      t9 = t11;
      break bb1;
    }
    t9 = style;
  }
  const waveViewStyle = t9;
  let t10;
  bb2: {
    if (!spaces.measurementsComplete) {
      let t11;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = {};
        $[16] = t11;
      } else {
        t11 = $[16];
      }
      t10 = t11;
      break bb2;
    }
    let t11;
    if ($[17] !== spaces) {
      t11 = calculateHeightStyle(spaces, 0);
      $[17] = spaces;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    t10 = t11;
  }
  const leaderboardViewStyle = t10;
  let t11;
  bb3: {
    if (!spaces.measurementsComplete) {
      let t12;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = {};
        $[19] = t12;
      } else {
        t12 = $[19];
      }
      t11 = t12;
      break bb3;
    }
    let t12;
    if ($[20] !== spaces) {
      t12 = calculateHeightStyle(spaces, 0);
      $[20] = spaces;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    t11 = t12;
  }
  const winnersViewStyle = t11;
  let t12;
  bb4: {
    if (!spaces.measurementsComplete) {
      let t13;
      if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = {};
        $[22] = t13;
      } else {
        t13 = $[22];
      }
      t12 = t13;
      break bb4;
    }
    let t13;
    if ($[23] !== spaces) {
      t13 = calculateHeightStyle(spaces, 0);
      $[23] = spaces;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    t12 = t13;
  }
  const myVotesViewStyle = t12;
  let t13;
  bb5: {
    if (!spaces.measurementsComplete) {
      let t14;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = {};
        $[25] = t14;
      } else {
        t14 = $[25];
      }
      t13 = t14;
      break bb5;
    }
    let t14;
    if ($[26] !== spaces) {
      t14 = calculateHeightStyle(spaces, 0);
      $[26] = spaces;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    t13 = t14;
  }
  const outcomeViewStyle = t13;
  let t14;
  bb6: {
    if (!spaces.measurementsComplete) {
      let t15;
      if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = {};
        $[28] = t15;
      } else {
        t15 = $[28];
      }
      t14 = t15;
      break bb6;
    }
    let t15;
    if ($[29] !== spaces) {
      t15 = calculateHeightStyle(spaces, 0);
      $[29] = spaces;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    t14 = t15;
  }
  const faqViewStyle = t14;
  let t15;
  bb7: {
    if (!spaces.measurementsComplete) {
      let t16;
      if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = {};
        $[31] = t16;
      } else {
        t16 = $[31];
      }
      t15 = t16;
      break bb7;
    }
    let t16;
    if ($[32] !== spaces) {
      t16 = calculateHeightStyle(spaces, 0);
      $[32] = spaces;
      $[33] = t16;
    } else {
      t16 = $[33];
    }
    t15 = t16;
  }
  const notificationsViewStyle = t15;
  let t16;
  bb8: {
    if (!spaces.measurementsComplete) {
      let t17;
      if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = {};
        $[34] = t17;
      } else {
        t17 = $[34];
      }
      t16 = t17;
      break bb8;
    }
    let t17;
    if ($[35] !== spaces) {
      t17 = calculateHeightStyle(spaces, 0);
      $[35] = spaces;
      $[36] = t17;
    } else {
      t17 = $[36];
    }
    t16 = t17;
  }
  const myStreamFeedStyle = t16;
  let t17;
  bb9: {
    if (!spaces.measurementsComplete) {
      let t18;
      if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = {};
        $[37] = t18;
      } else {
        t18 = $[37];
      }
      t17 = t18;
      break bb9;
    }
    let t18;
    if ($[38] !== spaces) {
      const homepageSpaces = {
        ...spaces,
        headerSpace: 0,
        spacerSpace: 0
      };
      t18 = calculateHeightStyle(homepageSpaces, 0);
      $[38] = spaces;
      $[39] = t18;
    } else {
      t18 = $[39];
    }
    t17 = t18;
  }
  const homepageFeedStyle = t17;
  let t18;
  bb10: {
    if (!spaces.measurementsComplete) {
      let t19;
      if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = {};
        $[40] = t19;
      } else {
        t19 = $[40];
      }
      t18 = t19;
      break bb10;
    }
    const totalOffset = spaces.headerSpace + spaces.pinnedSpace + spaces.tabsSpace + spaces.spacerSpace + spaces.mobileTabsSpace + spaces.mobileNavSpace;
    const heightCalc = `calc(100dvh - ${totalOffset}px)`;
    let t19;
    if ($[41] !== heightCalc) {
      t19 = {
        height: heightCalc,
        maxHeight: heightCalc
      };
      $[41] = heightCalc;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    t18 = t19;
  }
  const smallScreenFeedStyle = t18;
  let t19;
  bb11: {
    if (!spaces.measurementsComplete) {
      let t20;
      if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
        t20 = {};
        $[43] = t20;
      } else {
        t20 = $[43];
      }
      t19 = t20;
      break bb11;
    }
    let t20;
    if ($[44] !== spaces) {
      t20 = calculateHeightStyle(spaces, 0);
      $[44] = spaces;
      $[45] = t20;
    } else {
      t20 = $[45];
    }
    t19 = t20;
  }
  const mobileWavesViewStyle = t19;
  let t20;
  bb12: {
    if (!spaces.measurementsComplete) {
      let t21;
      if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = {};
        $[46] = t21;
      } else {
        t21 = $[46];
      }
      t20 = t21;
      break bb12;
    }
    let t21;
    if ($[47] !== spaces) {
      t21 = calculateHeightStyle(spaces, 0);
      $[47] = spaces;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    t20 = t21;
  }
  const mobileAboutViewStyle = t20;
  let t21;
  if ($[49] !== contentContainerStyle || $[50] !== faqViewStyle || $[51] !== homepageFeedStyle || $[52] !== leaderboardViewStyle || $[53] !== mobileAboutViewStyle || $[54] !== mobileWavesViewStyle || $[55] !== myStreamFeedStyle || $[56] !== myVotesViewStyle || $[57] !== notificationsViewStyle || $[58] !== outcomeViewStyle || $[59] !== smallScreenFeedStyle || $[60] !== spaces || $[61] !== waveViewStyle || $[62] !== winnersViewStyle) {
    t21 = {
      spaces,
      registerRef,
      contentContainerStyle,
      waveViewStyle,
      leaderboardViewStyle,
      winnersViewStyle,
      myVotesViewStyle,
      outcomeViewStyle,
      faqViewStyle,
      notificationsViewStyle,
      myStreamFeedStyle,
      homepageFeedStyle,
      smallScreenFeedStyle,
      mobileWavesViewStyle,
      mobileAboutViewStyle
    };
    $[49] = contentContainerStyle;
    $[50] = faqViewStyle;
    $[51] = homepageFeedStyle;
    $[52] = leaderboardViewStyle;
    $[53] = mobileAboutViewStyle;
    $[54] = mobileWavesViewStyle;
    $[55] = myStreamFeedStyle;
    $[56] = myVotesViewStyle;
    $[57] = notificationsViewStyle;
    $[58] = outcomeViewStyle;
    $[59] = smallScreenFeedStyle;
    $[60] = spaces;
    $[61] = waveViewStyle;
    $[62] = winnersViewStyle;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  const contextValue = t21;
  let t22;
  if ($[64] !== children || $[65] !== contextValue) {
    t22 = <LayoutContext.Provider value={contextValue}>{children}</LayoutContext.Provider>;
    $[64] = children;
    $[65] = contextValue;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  return t22;
};

// Custom hook to use the layout context
export const useLayout = () => {
  return useContext(LayoutContext);
};
function _temp() {}